import { useState } from 'react'
import { toast } from 'react-toastify'
import { PiPlus, PiX } from 'react-icons/pi'

import useApiService from 'services/api'
import FbButton from 'components/FbUI/FbButton'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { cn } from 'components/UI/cn'

// Keep in sync with MAX_USER_RECOMMENDATION_NOTES and the content max_length in
// server/recommendations/models.py (UserRecommendationNote).
const MAX_NOTES = 10
const MAX_NOTE_LENGTH = 1000

// Keep in sync with UserRecommendationNoteCategory
// (server/recommendations/models.py).
const NOTE_CATEGORIES = [
  { value: 'global', label: 'Global' },
  { value: 'work_role', label: 'Work Role Details' },
  { value: 'message_tone', label: 'Message Tone' },
] as const

/**
 * Dynamic per-user recommendation notes ("memories", FIR-7479), organized by
 * category tabs (Global / Work Role Details / Message Tone). Users add/remove
 * free-form notes that feed into recommendation synthesis. Shared by the AI
 * settings page (self) and the admin Users-detail accordion (`userId` targets
 * another user).
 *
 * @param userId - Target user for admin editing; omit to edit your own notes.
 */
export function RecommendationNotesEditor({ userId }: { userId?: number }) {
  const api = useApiService()
  const { data: notes, isLoading } = api.useGetRecommendationNotes(userId)
  const addNote = api.useAddRecommendationNote(userId)
  const deleteNote = api.useDeleteRecommendationNote(userId)
  const [draft, setDraft] = useState('')
  const [activeCategory, setActiveCategory] = useState<string>(
    NOTE_CATEGORIES[0].value
  )
  // Track in-flight deletes per note id so one delete doesn't disable every
  // other card's remove button (deleteNote is a single shared mutation).
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set())

  const allNotes = notes ?? []
  const visibleNotes = allNotes.filter((n) => n.category === activeCategory)
  // The 10-note cap is across all categories (bounds the synthesis prompt).
  const atCap = allNotes.length >= MAX_NOTES
  const canAdd = draft.trim().length > 0 && !atCap && !addNote.isPending

  const handleAdd = async () => {
    if (!canAdd) return
    try {
      await addNote.mutateAsync({
        content: draft.trim(),
        category: activeCategory,
      })
      setDraft('')
    } catch {
      toast.error('Failed to add note')
    }
  }

  const handleDelete = async (id: number) => {
    setDeletingIds((prev) => new Set(prev).add(id))
    try {
      await deleteNote.mutateAsync(id)
    } catch {
      toast.error('Failed to remove note')
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  return (
    <div>
      <div className="flex gap-1 border-b border-line mb-3">
        {NOTE_CATEGORIES.map((cat) => {
          const count = allNotes.filter((n) => n.category === cat.value).length
          const active = cat.value === activeCategory
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => setActiveCategory(cat.value)}
              className={cn(
                '-mb-px border-b-2 px-3 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'border-brand text-brand'
                  : 'border-transparent text-fg-secondary hover:text-fg'
              )}
            >
              {cat.label}
              {count > 0 && (
                <span className="ml-1.5 text-xs text-fg-tertiary">{count}</span>
              )}
            </button>
          )
        })}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          maxLength={MAX_NOTE_LENGTH}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void handleAdd()
            }
          }}
          placeholder='Add a note, e.g. "Keep recommendations brief and direct"'
          disabled={atCap}
          className="flex-1 rounded-lg border border-line bg-surface p-2 text-sm text-fg placeholder:text-fg-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-50"
        />
        <Ds2026ButtonProvider>
          <FbButton
            type="button"
            variant="primary"
            onClick={handleAdd}
            disabled={!canAdd}
            loading={addNote.isPending}
          >
            <PiPlus size={16} /> Add
          </FbButton>
        </Ds2026ButtonProvider>
      </div>
      <div className="flex justify-between mt-1">
        <p className="text-xs text-fg-tertiary">
          {atCap ? `Limit of ${MAX_NOTES} notes reached` : ''}
        </p>
        <p className="text-xs text-fg-tertiary">
          {draft.length}/{MAX_NOTE_LENGTH}
        </p>
      </div>

      <div className="mt-3 space-y-2">
        {isLoading ? (
          <p className="text-sm text-fg-secondary">Loading…</p>
        ) : visibleNotes.length === 0 ? (
          <p className="text-sm text-fg-tertiary italic">
            No notes in this category yet.
          </p>
        ) : (
          visibleNotes.map((note) => (
            <div
              key={note.id}
              className="flex items-start justify-between gap-3 rounded-lg border border-line bg-surface p-3"
            >
              <p className="text-sm text-fg whitespace-pre-wrap break-words">
                {note.content}
              </p>
              <button
                type="button"
                aria-label="Remove note"
                onClick={() => handleDelete(note.id)}
                disabled={deletingIds.has(note.id)}
                className="shrink-0 rounded p-1 text-fg-tertiary hover:bg-surface-sunken hover:text-fg-secondary disabled:opacity-50"
              >
                <PiX size={16} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
